import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Layer,
} from "recharts";

const formatClp = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v ?? "");
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);
};

const COLORS = ["#7c3aed", "#2563eb", "#059669", "#d97706", "#dc2626"];
const COMPARE_PURPLE_RECENT = "#7c3aed"; // morado
const COMPARE_PURPLE_OLDER = "#a78bfa"; // morado más claro

/** Tortas Pascale: tonos medios (más cuerpo que pastel), armónicos con el morado del producto */
const PIE_SLICE_COLORS = [
  "#8b5cf6",
  "#6366f1",
  "#3b82f6",
  "#0ea5e9",
  "#14b8a6",
  "#22c55e",
  "#ca8a04",
  "#ea580c",
  "#e11d48",
  "#a855f7",
  "#64748b",
  "#475569",
];

/**
 * "Otros" solo agrupa la cola de menor monto mientras la suma de esas porciones no supere `maxCombinedSharePct`
 * del total (p. ej. 3%). Así no se mezclan muchos proveedores/clientes en un bloque grande.
 * Requiere al menos 2 contrapartes en la cola para crear el segmento agrupado.
 */
const PIE_OTHERS_MAX_COMBINED_SHARE_PCT = 3;

function mergePieTailIntoOthers(rows, valueKey, maxCombinedSharePct = PIE_OTHERS_MAX_COMBINED_SHARE_PCT) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;
  const clean = rows.filter((r) => Math.abs(Number(r?.[valueKey]) || 0) > 0);
  if (clean.length < 2) return rows;
  const total = clean.reduce((s, r) => s + Math.abs(Number(r[valueKey]) || 0), 0);
  if (!(total > 0)) return rows;

  const maxSum = (maxCombinedSharePct / 100) * total;
  const sortedAsc = [...clean].sort(
    (a, b) => Math.abs(Number(a[valueKey]) || 0) - Math.abs(Number(b[valueKey]) || 0),
  );

  const othersRows = [];
  let sumO = 0;
  for (const row of sortedAsc) {
    const v = Math.abs(Number(row[valueKey]) || 0);
    if (v <= 0) continue;
    if (sumO + v <= maxSum) {
      othersRows.push(row);
      sumO += v;
    } else {
      break;
    }
  }

  if (othersRows.length < 2 || sumO <= 0) return clean;

  const othersSet = new Set(othersRows);
  const kept = clean.filter((r) => !othersSet.has(r));

  const names = othersRows.map((r) => String(r?.name ?? "").trim()).filter(Boolean);
  const namesPart =
    names.length <= 8 ? names.join(", ") : `${names.slice(0, 6).join(", ")} (+${names.length - 6} más)`;
  const pieOtherDetail = `Cola menor (en conjunto ≤${maxCombinedSharePct}% del total): ${namesPart}`;

  const othersRow = {
    name: "Otros",
    [valueKey]: sumO,
    pieOtherDetail,
  };

  return [...kept, othersRow].sort(
    (a, b) => Math.abs(Number(b[valueKey]) || 0) - Math.abs(Number(a[valueKey]) || 0),
  );
}

function preparePieChartData(rows, valueKey) {
  return mergePieTailIntoOthers(rows, valueKey, PIE_OTHERS_MAX_COMBINED_SHARE_PCT);
}

function shortenChartLabel(raw, maxLen = 26) {
  const s = String(raw ?? "").trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen - 1)}…`;
}

/** Igual que Recharts `mathSign` (ángulos del Pie). */
function mathSign(value) {
  if (value === 0 || Number.isNaN(value)) return 0;
  return value > 0 ? 1 : -1;
}

/**
 * Replica la repartición angular de Recharts (Pie.getComposedData) para alinear líneas con las porciones.
 */
function computePieSectorsAngles(pieData, dataKey, { startAngle = 0, endAngle = 360, paddingAngle = 0, minAngle = 0 } = {}) {
  const getVal = (e) => {
    const v = Number(e?.[dataKey]);
    return Number.isFinite(v) ? v : 0;
  };

  const deltaAngle = endAngle - startAngle;
  const dir = mathSign(deltaAngle);
  const absDeltaAngle = Math.abs(deltaAngle);
  const notZeroItemCount = pieData.filter((e) => getVal(e) !== 0).length;
  const totalPaddingAngle = (absDeltaAngle >= 360 ? notZeroItemCount : Math.max(0, notZeroItemCount - 1)) * paddingAngle;
  const realTotalAngle = absDeltaAngle - notZeroItemCount * minAngle - totalPaddingAngle;
  const sum = pieData.reduce((s, e) => s + getVal(e), 0);

  if (!(sum > 0)) {
    return pieData.map((entry, index) => ({
      midAngle: 0,
      percent: 0,
      name: entry?.name,
      index,
      value: 0,
    }));
  }

  let prevSector = null;
  return pieData.map((entry, i) => {
    const val = getVal(entry);
    const percent = val / sum;
    let tempStartAngle;
    if (i && prevSector) {
      tempStartAngle = prevSector.endAngle + dir * paddingAngle * (val !== 0 ? 1 : 0);
    } else {
      tempStartAngle = startAngle;
    }
    const tempEndAngle = tempStartAngle + dir * ((val !== 0 ? minAngle : 0) + percent * realTotalAngle);
    const midAngle = (tempStartAngle + tempEndAngle) / 2;
    const sector = {
      startAngle: tempStartAngle,
      endAngle: tempEndAngle,
      midAngle,
      percent,
      name: entry?.name,
      index: i,
      value: val,
    };
    prevSector = sector;
    return sector;
  });
}

/**
 * Coloca etiquetas por lado (der/izq/arriba/abajo) y separa en Y para evitar solapes (top 20+).
 */
function layoutPieOutsideLabels(sectorsAngles, { cx, cy, outerRadius, dense }) {
  const RADIAN = Math.PI / 180;
  const cosThr = 0.08;
  const arm = dense ? 36 : 44;
  const minGap = dense ? 12 : 14;
  const nameMax = dense ? 18 : 24;

  const raw = [];
  for (const s of sectorsAngles) {
    const { midAngle, percent, name, index } = s;
    if (percent < 0.012) continue;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 6) * cos;
    const sy = cy + (outerRadius + 6) * sin;
    const mx = cx + (outerRadius + 18) * cos;
    const my = cy + (outerRadius + 18) * sin;
    const textAnchor = cos >= 0 ? "start" : "end";
    const ex = mx + (cos >= 0 ? 1 : -1) * arm;
    let side;
    if (cos >= cosThr) side = "right";
    else if (cos <= -cosThr) side = "left";
    else if (sin < 0) side = "top";
    else side = "bottom";

    raw.push({ index, name, percent, sx, sy, mx, my, ex, textAnchor, side });
  }

  for (const g of raw) {
    g.ey = g.my;
  }

  function resolveVertical(group) {
    if (group.length <= 1) return;
    group.sort((a, b) => a.my - b.my);
    let prevEy = group[0].my - minGap * 2;
    for (const g of group) {
      g.ey = Math.max(g.my, prevEy + minGap);
      prevEy = g.ey;
    }
  }

  resolveVertical(raw.filter((x) => x.side === "right"));
  resolveVertical(raw.filter((x) => x.side === "left"));
  resolveVertical(raw.filter((x) => x.side === "top"));
  resolveVertical(raw.filter((x) => x.side === "bottom"));

  for (const g of raw) {
    g.labelText = `${shortenChartLabel(g.name, nameMax)} (${(g.percent * 100).toFixed(0)}%)`;
  }
  return raw.sort((a, b) => a.index - b.index);
}

function getPiePlotBox(widthPx, heightPx, margin) {
  const innerW = Math.max(80, widthPx - margin.left - margin.right);
  const innerH = Math.max(80, heightPx - margin.top - margin.bottom);
  const maxPieRadius = Math.min(innerW, innerH) / 2;
  const cx = margin.left + innerW / 2;
  const cy = margin.top + innerH / 2;
  return { cx, cy, maxPieRadius, innerW, innerH };
}

const ChartTooltip = ({ active, payload, label, series }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ai-sidechat__chartTooltip">
      <div className="ai-sidechat__chartTooltip__label">{label}</div>
      {payload.map((p, i) => {
        const meta = series?.find((s) => s.dataKey === p.dataKey);
        const fmt =
          meta?.format === "clp" ? (v) => (v == null ? "—" : formatClp(v)) : (x) => (x == null || x === "" ? "—" : x);
        return (
          <div key={p.dataKey || i}>
            {p.name}: {fmt(p.value)}
            {p.payload?.variationPctMoM != null && p.dataKey === series?.[0]?.dataKey ? (
              <span className="ai-sidechat__chartTooltip__mom">
                {" "}
                (MoM: {Number(p.payload.variationPctMoM).toFixed(1)}%)
              </span>
            ) : null}
            {p.payload?.pieOtherDetail ? (
              <div className="ai-sidechat__chartTooltip__detail">{p.payload.pieOtherDetail}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

function PieBlockWithSmartLabels({
  title,
  pieData,
  primaryKey,
  series,
  pieH,
  margin,
  desiredOuterRadius,
  dense,
  paddingAngle,
  chartAnimationReady,
  animationKey,
}) {
  const wrapRef = useRef(null);
  const [widthPx, setWidthPx] = useState(0);
  const [labelsShown, setLabelsShown] = useState(() => !chartAnimationReady);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const read = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidthPx(w);
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!chartAnimationReady) {
      setLabelsShown(true);
      return undefined;
    }
    setLabelsShown(false);
    const t = window.setTimeout(() => setLabelsShown(true), 950);
    return () => window.clearTimeout(t);
  }, [chartAnimationReady, animationKey, pieData, primaryKey]);

  const layoutModel = useMemo(() => {
    const w = widthPx > 0 ? widthPx : 400;
    const { cx, cy, maxPieRadius } = getPiePlotBox(w, pieH, margin);
    const outerRadius = Math.round(Math.min(desiredOuterRadius, Math.max(48, maxPieRadius - 6)));
    const sectorsAngles = computePieSectorsAngles(pieData, primaryKey, { paddingAngle });
    const labels = layoutPieOutsideLabels(sectorsAngles, { cx, cy, outerRadius, dense });
    return { outerRadius, labels };
  }, [widthPx, pieH, margin, desiredOuterRadius, pieData, primaryKey, paddingAngle, dense]);

  const handlePieAnimEnd = () => {
    if (chartAnimationReady) setLabelsShown(true);
  };

  return (
    <div
      ref={wrapRef}
      className={`ai-sidechat__chartBlock ai-sidechat__chartBlock--pie${dense ? " ai-sidechat__chartBlock--pieDense" : ""}`}
    >
      <div className="ai-sidechat__chartTitle">{title}</div>
      <div className="ai-sidechat__chartCanvas ai-sidechat__chartCanvas--pie">
        <ResponsiveContainer width="100%" height={pieH}>
          <PieChart margin={margin}>
            <Pie
              data={pieData}
              dataKey={primaryKey}
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius={layoutModel.outerRadius}
              paddingAngle={paddingAngle}
              stroke="#ffffff"
              strokeWidth={2}
              label={false}
              labelLine={false}
              isAnimationActive={chartAnimationReady}
              animationBegin={0}
              animationDuration={900}
              animationEasing="ease-out"
              onAnimationEnd={handlePieAnimEnd}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${entry.name}-${index}`} fill={PIE_SLICE_COLORS[index % PIE_SLICE_COLORS.length]} />
              ))}
            </Pie>
            {labelsShown && layoutModel.labels.length ? (
              <Layer className="ai-sidechat__pieSmartLabels" style={{ pointerEvents: "none" }}>
                {layoutModel.labels.map((l) => (
                  <g
                    key={`pie-lbl-${l.index}`}
                    className={`ai-sidechat__pieLabelGroup${dense ? " ai-sidechat__pieLabelGroup--dense" : ""}`}
                  >
                    <path
                      d={
                        Math.abs(l.ey - l.my) < 0.5
                          ? `M${l.sx},${l.sy}L${l.mx},${l.my}L${l.ex},${l.ey}`
                          : `M${l.sx},${l.sy}L${l.mx},${l.my}L${l.ex},${l.my}L${l.ex},${l.ey}`
                      }
                      stroke="#cbd5e1"
                      fill="none"
                      strokeWidth={1}
                    />
                    <text
                      x={l.ex}
                      y={l.ey}
                      dy={3}
                      textAnchor={l.textAnchor}
                      className={`ai-sidechat__pieOutsideText${dense ? " ai-sidechat__pieOutsideText--dense" : ""}`}
                    >
                      {l.labelText}
                    </text>
                  </g>
                ))}
              </Layer>
            ) : null}
            <Tooltip content={(props) => <ChartTooltip {...props} series={series} />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function inferYearFromSeriesName(name) {
  const m = String(name ?? "").match(/\b(20\d{2})\b/);
  if (!m) return null;
  const y = Number(m[1]);
  return Number.isFinite(y) ? y : null;
}

function getSeriesColor(series, s, i) {
  // Para comparación anual (2 curvas): año más reciente morado, año previo morado claro.
  if (Array.isArray(series) && series.length === 2) {
    const y0 = inferYearFromSeriesName(series[0]?.name);
    const y1 = inferYearFromSeriesName(series[1]?.name);
    if (y0 != null && y1 != null) {
      const recent = Math.max(y0, y1);
      const y = inferYearFromSeriesName(s?.name);
      if (y === recent) return COMPARE_PURPLE_RECENT;
      return COMPARE_PURPLE_OLDER;
    }
  }
  return COLORS[i % COLORS.length];
}

export function buildChartsFromDetails(details) {
  if (!details || typeof details !== "object") return [];
  if (Array.isArray(details.charts) && details.charts.length) {
    return details.charts;
  }
  const dr = details.dataResult;
  if (!dr) return [];
  const rows = Array.isArray(dr.byCounterparty)
    ? dr.byCounterparty
    : Array.isArray(dr.breakdown)
      ? dr.breakdown.map((b) => ({
          name: String(b.label || b.name || ""),
          totalAmount: Number(b.totalAmount ?? 0) || 0,
        }))
      : [];
  if (rows.length < 2) return [];
  return [
    {
      type: "bar",
      title: "Distribución por contraparte (consulta actual)",
      data: rows.slice(0, 20).map((x) => ({
        name: String(x.name || "").slice(0, 36),
        monto: Number(x.totalAmount ?? 0) || 0,
      })),
      series: [{ dataKey: "monto", name: "Monto", format: "clp" }],
    },
  ];
}

function formatCellValue(v, seriesMeta) {
  if (v == null || (typeof v === "number" && Number.isNaN(v))) return "—";
  if (seriesMeta?.format === "clp") return formatClp(v);
  return String(v);
}

export function ChartDataTable({ config, enableDocumentLinks, onDocumentLinkClick }) {
  const { title, data, series, labelHeader, documentLinks } = config;
  if (!Array.isArray(data) || !data.length || !Array.isArray(series) || !series.length) return null;
  const hasMom = data.some((r) => r != null && r.variationPctMoM != null);
  const linksOn =
    enableDocumentLinks &&
    documentLinks &&
    typeof onDocumentLinkClick === "function";

  const renderFolioCell = (row) => {
    const label = row.name != null ? String(row.name) : "—";
    const mongoId = row.mongoId ? String(row.mongoId) : "";
    if (!linksOn || !mongoId) return label;
    return (
      <button
        type="button"
        className="ai-sidechat__folioLink"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDocumentLinkClick(mongoId, documentLinks);
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="ai-sidechat__chartBlock ai-sidechat__chartBlock--table">
      <div className="ai-sidechat__chartTitle">{title}</div>
      <div className="ai-sidechat__tableWrap">
        <table className="ai-sidechat__dataTable">
          <thead>
            <tr>
              <th scope="col">{labelHeader || "Etiqueta"}</th>
              {series.map((s) => (
                <th key={s.dataKey} scope="col">
                  {s.name}
                </th>
              ))}
              {hasMom ? (
                <th key="mom" scope="col">
                  Var. MoM %
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {data.map((row, ri) => (
              <tr key={ri}>
                <td>{renderFolioCell(row)}</td>
                {series.map((s) => (
                  <td key={s.dataKey}>{formatCellValue(row[s.dataKey], s)}</td>
                ))}
                {hasMom ? (
                  <td>{row.variationPctMoM != null ? `${Number(row.variationPctMoM).toFixed(1)}%` : "—"}</td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartBlock({
  config,
  barHeight,
  lineHeight,
  chartAnimationReady,
  animationKey,
  enableDocumentLinks,
  onDocumentLinkClick,
}) {
  const { type, title, data, series } = config;
  if (!Array.isArray(data) || !data.length || !Array.isArray(series) || !series.length) return null;

  // KPI de un solo valor: el reply ya muestra el número, no hay gráfico útil
  if (type === "kpi") return null;

  if (type === "table") {
    return (
      <ChartDataTable
        config={config}
        enableDocumentLinks={enableDocumentLinks}
        onDocumentLinkClick={onDocumentLinkClick}
      />
    );
  }

  if (type === "pie") {
    const primaryKey = series[0]?.dataKey;
    if (!primaryKey) return null;
    const pieData = preparePieChartData(data, primaryKey);
    const n = pieData.length;
    const dense = n >= 9;
    const pieH = Math.max(lineHeight, Math.min(720, 168 + n * 26));
    const margin =
      n >= 16
        ? { top: 56, right: 96, bottom: 56, left: 96 }
        : n >= 12
          ? { top: 52, right: 84, bottom: 52, left: 84 }
          : n >= 9
            ? { top: 44, right: 68, bottom: 44, left: 68 }
            : { top: 36, right: 52, bottom: 36, left: 52 };
    const desiredOuterRadius = Math.round(
      Math.min(pieH * (n >= 12 ? 0.26 : n >= 9 ? 0.29 : 0.34), n >= 12 ? 100 : n >= 9 ? 112 : 128),
    );
    return (
      <PieBlockWithSmartLabels
        title={title}
        pieData={pieData}
        primaryKey={primaryKey}
        series={series}
        pieH={pieH}
        margin={margin}
        desiredOuterRadius={desiredOuterRadius}
        dense={dense}
        paddingAngle={n >= 12 ? 1.2 : 1.5}
        chartAnimationReady={chartAnimationReady}
        animationKey={animationKey}
      />
    );
  }

  if (type === "bar") {
    const nBars = data.length;
    const bottomMargin = Math.min(132, 76 + Math.min(56, Math.ceil(nBars / 4) * 6));
    const xAxisHeight = Math.min(110, 68 + Math.min(40, Math.ceil(nBars / 5) * 5));
    return (
      <div className="ai-sidechat__chartBlock ai-sidechat__chartBlock--bar">
        <div className="ai-sidechat__chartTitle">{title}</div>
        <div className="ai-sidechat__chartCanvas ai-sidechat__chartCanvas--bar">
          <ResponsiveContainer width="100%" height={barHeight}>
            <BarChart data={data} margin={{ top: 12, right: 14, left: 8, bottom: bottomMargin }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-32}
                textAnchor="end"
                height={xAxisHeight}
                tickMargin={10}
              />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => formatClp(v).replace(/\s/g, "\u00A0")} width={78} />
              <Tooltip content={(props) => <ChartTooltip {...props} series={series} />} />
              {series.map((s, i) => (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.name}
                  fill={COLORS[i % COLORS.length]}
                  radius={[6, 6, 0, 0]}
                  isAnimationActive={chartAnimationReady}
                  animationBegin={0}
                  animationDuration={900}
                  animationEasing="ease-out"
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-sidechat__chartBlock">
      <div className="ai-sidechat__chartTitle">{title}</div>
      <div className="ai-sidechat__chartCanvas">
        <ResponsiveContainer width="100%" height={lineHeight}>
          <LineChart data={data} margin={{ top: 12, right: 20, left: 4, bottom: 8 }} key={animationKey}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => formatClp(v).replace(/\s/g, "\u00A0")} width={76} stroke="#94a3b8" />
            <Tooltip content={(props) => <ChartTooltip {...props} series={series} />} />
            {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} /> : null}
            {series.map((s, i) => (
              <Line
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name}
                stroke={getSeriesColor(series, s, i)}
                strokeWidth={2.5}
                dot={{ r: 4, strokeWidth: 2, fill: "#fff" }}
                activeDot={{ r: 6 }}
                connectNulls={false}
                isAnimationActive={chartAnimationReady}
                animationBegin={0}
                animationDuration={1500}
                animationEasing="ease-in-out"
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/**
 * Gráficos desde `details` (respuesta aiGatewayChat).
 * @param {boolean} panelMode — layout expandido (más alto, estilos panel).
 * @param {boolean} chartAnimationReady — si es false, la curva no se anima hasta activarse (tras abrir el panel).
 * @param {string} animationKey — cambia para forzar nueva animación al actualizar datos.
 * @param {number} chartHeight — alto fijo opcional (p. ej. vista ampliada); si no, según panelMode.
 * @param {'chart'|'table'} viewMode — gráfico o tabla.
 */
const AiGatewayCharts = ({
  details,
  panelMode,
  chartAnimationReady = true,
  animationKey = "0",
  chartHeight,
  viewMode = "chart",
  enableDocumentLinks = false,
  onDocumentLinkClick,
}) => {
  const charts = useMemo(() => buildChartsFromDetails(details), [details]);
  if (!charts.length) return null;
  const hasBarChart = charts.some((c) => c?.type === "bar");
  const hasPieChart = charts.some((c) => c?.type === "pie");
  const barH =
    chartHeight != null
      ? Math.round(chartHeight * (hasBarChart ? 0.86 : 0.92))
      : panelMode
        ? hasBarChart
          ? 460
          : 300
        : hasBarChart
          ? 360
          : 240;
  const lineH =
    chartHeight != null
      ? chartHeight
      : panelMode
        ? hasPieChart
          ? 380
          : 320
        : hasPieChart
          ? 320
          : 260;
  if (viewMode === "table") {
    return (
      <div
        className={`ai-sidechat__charts ai-sidechat__charts--tables${panelMode ? " ai-sidechat__charts--panel" : ""}`}
        aria-label="Tablas de la consulta"
      >
        {charts.map((cfg, i) => (
          <ChartDataTable
            key={i}
            config={cfg}
            enableDocumentLinks={enableDocumentLinks}
            onDocumentLinkClick={onDocumentLinkClick}
          />
        ))}
      </div>
    );
  }
  return (
    <div
      className={`ai-sidechat__charts${panelMode ? " ai-sidechat__charts--panel" : ""}`}
      aria-label="Gráficos de la consulta"
    >
      {charts.map((cfg, i) => (
        <ChartBlock
          key={i}
          config={cfg}
          barHeight={barH}
          lineHeight={lineH}
          chartAnimationReady={chartAnimationReady}
          animationKey={`${animationKey}-${i}`}
        />
      ))}
    </div>
  );
};

export default AiGatewayCharts;
